/**
 * The error text a form field shows (Phase 5 PR 5.1, O164; Alex ruled A on
 * 2026-10-02): every field in an error state shows a message. The Helios
 * contract is a visible message plus `aria-invalid`; colour alone is not an
 * error state.
 *
 * - Text a field shows today stays word for word under its own condition:
 *   the caller passes it to FieldGroup's `error` (or ChoiceGroup's).
 * - Elsewhere the message comes from `fieldErrorMessage` below. A message
 *   the form already wrote as copy (a schema message such as "Invalid phone
 *   number" or "Date cannot be in the future", or one set with
 *   `setFieldError`) is shown as is. Yup's default messages name the field
 *   key ("firstName is a required field") and are never shown: a required
 *   field reads REQUIRED_MESSAGE and any other default CHECK_MESSAGE.
 *
 * The two messages and the class-times message are proposals for Content.
 */

/**
 * The one switch. With it `false`, a field that shows no text today shows
 * none: the control still gets `aria-invalid` and the danger edge, and the
 * existing messages still show.
 */
export const SHOW_FIELD_ERROR_TEXT = true

export const REQUIRED_MESSAGE = 'This field is required.'
export const CHECK_MESSAGE = 'Please check this entry.'

/**
 * The one message Field.js itself showed before PR 5.1 (on a touched
 * formatted field, the date of birth): it shows whatever the switch says.
 */
export const FUTURE_DATE_MESSAGE = 'Date cannot be in the future'

/** The class-times step's failed save (PR 5.1, defect 2). */
export const CLASS_TIMES_SAVE_FAILED_MESSAGE =
  "We couldn't save your preferred class times. Please choose another time or try again."

/**
 * Yup's default messages start with the field's path (its Formik name):
 * `${path} is a required field`, `${path} must be a valid email`,
 * `${path} must match the following: …`, `${path} must be a \`date\` type…`.
 */
function isYupDefault(name, message) {
  return Boolean(name) && message.startsWith(`${name} `)
}

/**
 * The message for a Formik error on field `name`, or undefined when there is
 * none to show.
 *
 * @param {string | undefined} name
 * @param {unknown} error
 * @returns {string | undefined}
 */
export function fieldErrorMessage(name, error) {
  if (typeof error !== 'string' || error === '') return undefined
  if (!isYupDefault(name, error)) return error
  return error === `${name} is a required field`
    ? REQUIRED_MESSAGE
    : CHECK_MESSAGE
}

/**
 * What a field shows: its existing text when the caller passes one (under
 * the caller's own condition, whatever the switch says); otherwise, while
 * the switch is on, the message for a touched field's error. A caller whose
 * existing text is the field's own error message, held back by its own
 * condition (the essay's "Min N characters" until the first submit), passes
 * `null`: no other text may stand in for it while it is held.
 *
 * @param {{
 *   name?: string,
 *   meta?: {touched?: boolean, error?: unknown},
 *   legacyError?: import('react').ReactNode,
 *   show?: boolean,
 * }} options
 */
export function shownFieldError({
  name,
  meta,
  legacyError,
  show = SHOW_FIELD_ERROR_TEXT,
}) {
  if (legacyError === null) return undefined
  if (legacyError) return legacyError
  if (meta?.touched && meta.error === FUTURE_DATE_MESSAGE) return meta.error
  if (!show || !meta?.touched) return undefined
  return fieldErrorMessage(name, meta.error)
}
